import { supabase, supabaseAnonKey, supabaseUrl } from "./supabase";

/**
 * POST create-contract via fetch so 500 JSON bodies (error, details, hint)
 * surface in admin toasts (supabase-js `invoke` swallows them). Shared by the
 * Tenants list dialog and the full-page tenant route.
 */
async function postCreateContract(
  requestBody: Record<string, unknown>
): Promise<Record<string, unknown>> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const base = (supabaseUrl || "").replace(/\/+$/, "");
  const res = await fetch(`${base}/functions/v1/create-contract`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${session?.access_token ?? ""}`,
      apikey: supabaseAnonKey,
    },
    body: JSON.stringify(requestBody),
  });
  const text = await res.text();
  let body: Record<string, unknown> = {};
  try {
    body = text ? (JSON.parse(text) as Record<string, unknown>) : {};
  } catch {
    body = { error: text?.slice(0, 200) || res.statusText };
  }
  if (!res.ok) {
    const err = new Error(
      String(body?.error || res.statusText || "Request failed")
    ) as Error & {
      details?: string;
      hint?: string;
      code?: string;
    };
    err.details = typeof body?.details === "string" ? body.details : undefined;
    err.hint = typeof body?.hint === "string" ? body.hint : undefined;
    err.code = typeof body?.code === "string" ? body.code : undefined;
    console.error("[admin create-contract]", res.status, body);
    throw err;
  }
  if (body?.error) {
    const err = new Error(String(body.error)) as Error & {
      details?: string;
      hint?: string;
    };
    err.details = typeof body.details === "string" ? body.details : undefined;
    err.hint = typeof body.hint === "string" ? body.hint : undefined;
    throw err;
  }
  return body;
}

/**
 * Did we just talk to a create-contract that predates the review flow?
 *
 * The deploy order is migrations → edge function → frontend, and the window in
 * between is exactly the accident this feature exists to prevent: an older
 * deployed function ignores `draft` entirely, so "Generate draft" RELEASES the
 * contract and emails the tenant a signing link while the toast says nothing was
 * sent. This is not hypothetical — it is what happens on any environment where
 * the function has not been redeployed yet.
 *
 * The new function echoes `mode` back. A response missing it, or one reporting a
 * status/email that draft mode can never produce, means the request reached the
 * old code — so say so loudly instead of reporting a success that did the
 * opposite.
 */
export function describeStaleContractFunction(
  body: Record<string, unknown>
): string | null {
  const emailed = body.contractReadyEmailSent === true;
  const status =
    typeof body.contractStatus === "string" ? body.contractStatus : null;

  if (body.mode === "draft" && status === "draft" && !emailed) return null;
  return [
    "The draft reached an OLD version of the create-contract function, which does not",
    "support draft mode:",
    status && status !== "draft"
      ? `the contract was released instead (status "${status}")`
      : "the contract may have been released",
    emailed
      ? "and the tenant WAS emailed a signing link."
      : "and the tenant may have been emailed.",
    "Deploy the create-contract edge function (`supabase functions deploy create-contract`),",
    "then check this booking's contract status before generating anything else.",
  ]
    .filter(Boolean)
    .join(" ");
}

/** Thrown when the deployed edge function is older than this frontend. */
export class StaleContractFunctionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "StaleContractFunctionError";
  }
}

/**
 * Build (or rebuild) the contract PDF for a booking.
 *
 * `draft: true` is the reviewed path: the PDF is generated and stored, the
 * booking parks at `contract_status = 'draft'`, and NOTHING reaches the tenant.
 * The admin reads the document, submits a verification note, and only then is
 * `invokeSendContractToTenant` unlocked.
 *
 * Without `draft` this keeps the historical one-step behaviour (generate AND
 * email immediately) — still used by the hidden legacy button.
 */
export async function invokeCreateContractForBooking(
  bookingId: number,
  opts?: {
    /** Override the contract template for this booking. */
    templateId?: string | null;
    /** Generate for review only — no email, tenant sees nothing. */
    draft?: boolean;
  }
): Promise<Record<string, unknown>> {
  const requestBody: Record<string, unknown> = { bookingId };
  if (opts?.templateId) requestBody.templateId = opts.templateId;
  if (opts?.draft) requestBody.draft = true;
  const body = await postCreateContract(requestBody);
  if (opts?.draft) {
    const stale = describeStaleContractFunction(body);
    if (stale) throw new StaleContractFunctionError(stale);
  }
  return body;
}

/**
 * Release a verified draft to the tenant: flips `contract_status` to
 * `ready_to_sign` and emails the signing link. Rebuilds nothing — the tenant
 * gets exactly the PDF the admin verified.
 *
 * The edge function rejects this for an unverified draft, so this is safe to
 * call from any surface; the UI gate is a convenience, not the only guard.
 */
export async function invokeSendContractToTenant(
  bookingId: number
): Promise<Record<string, unknown>> {
  return postCreateContract({ bookingId, notifyOnly: true });
}

/**
 * Approve a booking and prepare its contract as a DRAFT (never sent).
 * One flow for the tenants list and colivIQ Brain (COL-527). A refused
 * approval throws; a failed draft leaves the booking approved and is returned,
 * so each caller words its own warning.
 */
export async function approveBookingForContract(
  bookingId: number
): Promise<{ draftCreated: boolean; error?: unknown }> {
  // Only a PENDING booking: approving a live tenant would regress it to
  // `confirmed` and re-draft its contract. No row back (no longer pending,
  // RLS, deleted) fails here instead of drafting an unapproved contract.
  const { data, error } = await supabase
    .from("bookings")
    .update({ status: "confirmed" })
    .eq("id", bookingId)
    .eq("status", "pending")
    .select("id");
  if (error) throw error;
  if (!data?.length)
    throw new Error(
      "This booking is no longer pending — refresh to see its current status."
    );
  try {
    await invokeCreateContractForBooking(bookingId, { draft: true });
    return { draftCreated: true };
  } catch (e) {
    return { draftCreated: false, error: e };
  }
}

/**
 * create-contract's refusals that carry a stable `code`, worded in the
 * admin's language (the function's own `error` text is English only).
 * Null for anything else, so callers fall back to their usual message.
 */
export function contractRefusalMessage(
  e: unknown,
  t: (key: string, fallback?: string) => string
): string | null {
  const code = (e as { code?: unknown } | null)?.code;
  // COL-629: the tenant's date of birth on file makes them under 18.
  if (code === "TENANT_UNDER_MIN_AGE") {
    return t(
      "admin.tenants.contractTenantUnderAge",
      "The tenant must be at least 18 years old to sign a contract. Correct the date of birth first."
    );
  }
  return null;
}

/** A create-contract failure as toast copy: message — details, hint below. */
export function formatContractEdgeError(
  e: unknown,
  t: (key: string, fallback?: string) => string
): {
  title: string;
  description?: string;
} {
  const refusal = contractRefusalMessage(e, t);
  if (refusal) return { title: refusal };
  const x = e as Error & { details?: string; hint?: string };
  const title =
    [x?.message, x?.details].filter(Boolean).join(" — ") ||
    t("admin.tenants.contractRequestFailed", "Contract request failed");
  const description = x?.hint?.trim() || undefined;
  return { title, description };
}
